import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDismissableDialog } from './Modal';
import { useMessages } from '../i18n/I18n';

export interface DialogOptions {
  title?: string;
  /** The label of the button that confirms (or closes an alert) */
  confirmLabel?: string;
  cancelLabel?: string;
  /** A destructive confirmation: its confirm button is drawn as a danger */
  danger?: boolean;
}

export interface Dialogs {
  /** Asks a question in a dialog; resolves to true when confirmed, false when cancelled */
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>;
  /** Shows a message in a dialog; resolves when it's closed */
  alert: (message: string, options?: Omit<DialogOptions, 'cancelLabel' | 'danger'>) => Promise<void>;
}

interface DialogRequest extends DialogOptions {
  id: number;
  kind: 'confirm' | 'alert';
  message: string;
  answer: (confirmed: boolean) => void;
}

const DialogsContext = createContext<Dialogs | null>(null);

/** The in-app replacement of window.confirm / window.alert, for any screen under DialogsProvider */
export function useDialogs(): Dialogs {
  const dialogs = useContext(DialogsContext);
  if (!dialogs) throw new Error('useDialogs needs a DialogsProvider above it');
  return dialogs;
}

/** Shows the dialogs screens ask for, one at a time and in order, above every other dialog */
export const DialogsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const nextId = useRef(0);

  const ask = useCallback((kind: DialogRequest['kind'], message: string, options: DialogOptions = {}) =>
    new Promise<boolean>(resolve => {
      const id = nextId.current++;
      const answer = (confirmed: boolean) => {
        setQueue(requests => requests.filter(request => request.id !== id));
        resolve(confirmed);
      };
      setQueue(requests => [...requests, { ...options, id, kind, message, answer }]);
    }), []);

  const dialogs = useMemo<Dialogs>(() => ({
    confirm: (message, options) => ask('confirm', message, options),
    alert: async (message, options) => { await ask('alert', message, options); },
  }), [ask]);

  return (
    <DialogsContext.Provider value={dialogs}>
      {children}
      {/* A new key per request, so each one is a new dialog (focus, and the e2e tests see it appear) */}
      {queue.length > 0 && <Dialog key={queue[0].id} request={queue[0]} />}
    </DialogsContext.Provider>
  );
};

const Dialog: React.FC<{ request: DialogRequest }> = ({ request }) => {
  const { kind, message, answer, danger } = request;
  const t = useMessages('common');
  const title = request.title ?? (kind === 'confirm' ? t.confirmTitle : t.alertTitle);
  const confirmLabel = request.confirmLabel ?? t.ok;
  const cancelLabel = request.cancelLabel ?? t.cancel;
  const titleId = useId();
  const messageId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Escape or a click outside answers like Cancel; an alert just closes
  useDismissableDialog(true, contentRef, () => answer(false));

  // Focus goes to the confirm button, then back where it was
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    confirmRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, []);

  // Tab cycles through the dialog's buttons instead of reaching the page behind it
  const keepFocusInside = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const buttons = Array.from(e.currentTarget.querySelectorAll('button'));
    const focused = buttons.findIndex(button => button === document.activeElement);
    buttons[(focused + (e.shiftKey ? buttons.length - 1 : 1)) % buttons.length].focus();
  };

  return (
    <div className="modal-overlay app-dialog-overlay">
      <div
        className="modal-content app-dialog"
        ref={contentRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        tabIndex={-1}
        onKeyDown={keepFocusInside}
      >
        <h3 id={titleId}>{title}</h3>
        <p id={messageId} className="app-dialog-message">{message}</p>
        <div className="modal-actions">
          <button ref={confirmRef} type="button" className={danger ? 'danger' : undefined} onClick={() => answer(true)}>
            {confirmLabel}
          </button>
          {kind === 'confirm' && (
            <button type="button" onClick={() => answer(false)}>{cancelLabel}</button>
          )}
        </div>
      </div>
    </div>
  );
};
